import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAdminI18n } from '@/lib/i18n/use-admin-i18n';
import { roleApi } from '../../../../lib/api/role.api';
import type { Role } from '@cms/models';
import { useAuthStore } from '../../../../stores/authStore';
import { hasPermission } from '../../../../lib/rbac/permissionUtils';

const ALL_SCOPES = [
  { resource: 'user', actions: ['create', 'read', 'update', 'delete'] },
  { resource: 'role', actions: ['create', 'read', 'update', 'delete'] },
  { resource: 'product', actions: ['create', 'read', 'update', 'delete'] },
  { resource: 'category', actions: ['create', 'read', 'update', 'delete'] },
  { resource: 'brand', actions: ['create', 'read', 'update', 'delete'] },
  { resource: 'order', actions: ['create', 'read', 'update', 'delete', 'refund'] },
  { resource: 'inventory', actions: ['read', 'allocate', 'update'] },
  { resource: 'financial', actions: ['view', 'approve', 'transfer'] },
];

export function PermissionsPage() {
  const { page } = useAdminI18n();
  const { user } = useAuthStore();
  const [testScope, setTestScope] = useState('');

  const { data: roles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: () => roleApi.getAll(),
  });

  // User có một role (authStore.user.role) – backend có thể trả string hoặc object
  const role = user?.role;
  const userRoleId = typeof role === 'object' && role !== null ? role.id : undefined;
  const userRoles = userRoleId ? roles.filter(r => r.id === userRoleId) : [];
  const userScopes =
    (typeof role === 'object' && role !== null ? role.scopes : undefined) ??
    userRoles.flatMap(r => r.scopes);

  // Group scopes by resource
  const scopesByResource = ALL_SCOPES.map(scopeGroup => ({
    resource: scopeGroup.resource,
    scopes: scopeGroup.actions.map(action => `${scopeGroup.resource}:${action}`),
  }));

  // Check which roles have each scope
  const getRolesWithScope = (scope: string): Role[] => {
    return roles.filter(role => role.scopes.includes(scope));
  };

  const testResult = testScope ? hasPermission(userScopes, testScope) : null;

  return (
    <div className="space-y-2">
      <div>
        <h1 className="text-3xl font-bold text-foreground">{page('permissions', 'title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{page('permissions', 'subtitle')}</p>
      </div>

      {/* Permission Test Tool */}
      <div className="glass-card p-6">
        <h2 className="text-xl font-semibold mb-4 text-foreground">Test Permission</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Enter scope to test (e.g., product:create)
            </label>
            <input
              type="text"
              value={testScope}
              onChange={e => setTestScope(e.target.value)}
              placeholder="product:create"
              className="w-full px-4 py-2 bg-background border border-border rounded-md focus:ring-2 focus:ring-primary/20"
            />
          </div>
          {testResult !== null && (
            <div
              className={`p-4 rounded-lg border ${
                testResult
                  ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-600 dark:text-emerald-400'
                  : 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800 text-rose-600 dark:text-rose-400'
              }`}
            >
              {testResult ? (
                <div>
                  <p className="font-medium">✓ You have this permission</p>
                  <p className="text-sm mt-1">
                    Granted by roles:{' '}
                    {getRolesWithScope(testScope)
                      .map(r => r.name)
                      .join(', ')}
                  </p>
                </div>
              ) : (
                <p className="font-medium">✗ You do not have this permission</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* All Scopes */}
      <div className="glass-card p-6">
        <h2 className="text-xl font-semibold mb-4 text-foreground">All Available Scopes</h2>
        <div className="space-y-6">
          {scopesByResource.map(resourceGroup => (
            <div key={resourceGroup.resource}>
              <h3 className="font-medium text-foreground mb-3 capitalize text-lg">
                {resourceGroup.resource}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {resourceGroup.scopes.map(scope => {
                  const rolesWithScope = getRolesWithScope(scope);
                  const userHasPermission = hasPermission(userScopes, scope);
                  return (
                    <div
                      key={scope}
                      className={`p-3 border rounded-lg transition-colors ${
                        userHasPermission
                          ? 'border-primary/30 bg-primary/5 dark:bg-primary/10'
                          : 'border-border'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-mono text-foreground">{scope}</span>
                        {userHasPermission && (
                          <span className="text-xs bg-emerald-100 dark:bg-emerald-500/20 text-emerald-800 dark:text-emerald-300 px-2 py-1 rounded font-medium">
                            You have
                          </span>
                        )}
                      </div>
                      {rolesWithScope.length > 0 && (
                        <div className="mt-2 text-xs text-muted-foreground">
                          Roles: {rolesWithScope.map(r => r.name).join(', ')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
